import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui';
import { apiFetch } from '../../lib/api';
import type { Actor } from '../../lib/auth';

/**
 * THE HISTORY.
 *
 * This is a trust feature and it should look like one — visible, plain, and boring. Somebody opens
 * it when a director and a player disagree about Tuesday, and what they need is a sentence and a
 * name, not a diff.
 *
 * SO EVERY ROW IS ALREADY IN ENGLISH when it arrives. The translation happens in the shared module
 * both apps import, not here, because a screen that formats its own data is a screen that can
 * disagree with the next one.
 *
 * THE FILTERS OFFER ONLY WHAT EXISTS. A dropdown listing something the log has never contained is a
 * dead end that teaches people the filters do not work, so the options come from the rows this
 * actor can actually see.
 *
 * AND IT INTERPRETS NOTHING. No severity, no highlighting of "suspicious" edits, no ranking. The
 * log is evidence, and evidence that has already made up its mind is worth less.
 */

interface HistoryEntry {
  id: string;
  at: string;
  actorId: string | null;
  actorLabel: string | null;
  entityType: string;
  action: 'create' | 'update' | 'delete';
  leagueId: string | null;
  leagueName: string | null;
  said: string;
}

interface Options {
  actors: { id: string | null; label: string }[];
  entityTypes: string[];
  leagues: { id: string; name: string }[];
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The same de-snake-casing the sentence gets, for the one place a raw type is shown on purpose. */
function typeLabel(type: string): string {
  return type.replace(/_/g, ' ');
}

export function HistoryPage({ actor }: { actor: Actor }) {
  const isCommissioner = actor.role === 'commissioner';

  const [actorId, setActorId] = useState('');
  const [leagueId, setLeagueId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [since, setSince] = useState('');

  const params = new URLSearchParams();
  if (actorId) params.set('actorId', actorId);
  if (leagueId) params.set('leagueId', leagueId);
  if (entityType) params.set('entityType', entityType);
  if (since) params.set('since', since);

  const history = useQuery({
    queryKey: ['history', actorId, leagueId, entityType, since],
    queryFn: () =>
      apiFetch<{ entries: HistoryEntry[]; options: Options }>(
        `/api/history${params.toString() ? `?${params}` : ''}`,
      ),
  });

  if (history.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  const entries = history.data?.entries ?? [];
  const options = history.data?.options ?? { actors: [], entityTypes: [], leagues: [] };
  const filtered = actorId || leagueId || entityType || since;

  return (
    <div className="flex max-w-[76rem] flex-col gap-6 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">History</h1>
        <p className="mt-1 max-w-[46rem] text-[14px] leading-relaxed text-ink-2">
          {isCommissioner
            ? 'Every change anybody has made, with who made it and what it was before. Nothing here can be edited or deleted.'
            : 'Every change made to your leagues, with who made it and what it was before. Nothing here can be edited or deleted.'}
        </p>
      </header>

      <section className="flex flex-wrap items-end gap-3">
        <Filter label="Who">
          <select
            value={actorId}
            onChange={(event) => setActorId(event.target.value)}
            className="h-11 rounded-md border border-control-border bg-chalk px-3 text-[13px]"
          >
            <option value="">Anybody</option>
            {options.actors.map((option) => (
              <option key={option.label} value={option.id ?? ''}>
                {option.label}
              </option>
            ))}
          </select>
        </Filter>

        <Filter label="League">
          <select
            value={leagueId}
            onChange={(event) => setLeagueId(event.target.value)}
            className="h-11 rounded-md border border-control-border bg-chalk px-3 text-[13px]"
          >
            <option value="">Any league</option>
            {options.leagues.map((league) => (
              <option key={league.id} value={league.id}>
                {league.name}
              </option>
            ))}
          </select>
        </Filter>

        <Filter label="What">
          <select
            value={entityType}
            onChange={(event) => setEntityType(event.target.value)}
            className="h-11 rounded-md border border-control-border bg-chalk px-3 text-[13px] capitalize"
          >
            <option value="">Anything</option>
            {options.entityTypes.map((type) => (
              <option key={type} value={type}>
                {typeLabel(type)}
              </option>
            ))}
          </select>
        </Filter>

        <Filter label="Since">
          <input
            type="date"
            value={since}
            onChange={(event) => setSince(event.target.value)}
            className="tabular h-11 rounded-md border border-control-border bg-chalk px-3 text-[14px]"
          />
        </Filter>

        {filtered ? (
          <Button
            variant="secondary"
            onClick={() => {
              setActorId('');
              setLeagueId('');
              setEntityType('');
              setSince('');
            }}
          >
            Clear
          </Button>
        ) : null}
      </section>

      {entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
          {filtered ? 'Nothing matches those filters.' : 'Nothing has been changed yet.'}
        </p>
      ) : (
        <div
          className="overflow-x-auto"
          tabIndex={0}
          role="region"
          aria-label="History, scrollable"
        >
          {/* Read-only rows with nothing to tab to — see the same fix in `Directories.tsx`. */}
          <table className="w-full min-w-[44rem] border-collapse text-[14px]">
            <thead>
              <tr className="border-b border-stroke text-left">
                <Th className="w-40">When</Th>
                <Th className="w-44">Who</Th>
                <Th>What changed</Th>
                <Th className="w-48">League</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-light align-top">
                  <td className="tabular py-3 pr-4 text-[13px] text-ink-2">{when(entry.at)}</td>
                  {/*
                    The label rather than a join to `users`. Deactivating a director sets actor_id
                    to null, and the label is then the last readable trace of who did it.
                  */}
                  <td className="py-3 pr-4">{entry.actorLabel ?? 'Not recorded'}</td>
                  <td className="py-3 pr-4">{entry.said}</td>
                  <td className="py-3 text-[13px] text-ink-2">
                    {entry.leagueName ?? <span className="text-ink-3">Across the platform</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Said out loud, because a truncated list that does not say so is a list that lies. */}
      {entries.length >= 200 ? (
        <p className="text-[12px] text-ink-3">
          Showing the most recent 200. Narrow it down with the filters above to see further back.
        </p>
      ) : null}
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={[
        'pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}
